import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "@/api/client";

let onUnauthorized: (() => void) | null = null;
let unauthorizedInFlight = false; // single-flight mutex (D1)

/**
 * Registered by AuthProvider on mount, unregistered on unmount. Called at
 * most once per 401 "storm" (many in-flight queries failing simultaneously
 * on session expiry) — see `handleAuthError` below.
 */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
  unauthorizedInFlight = false; // reset on (re)login / provider remount
}

/** Test-only escape hatch to reset module-level mutex state between tests. */
export function resetUnauthorizedState() {
  unauthorizedInFlight = false;
}

function handleAuthError(error: unknown) {
  if (
    error instanceof ApiError &&
    error.status === 401 &&
    !unauthorizedInFlight
  ) {
    unauthorizedInFlight = true;
    queryClient.clear();
    onUnauthorized?.();
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) =>
        !(
          error instanceof ApiError &&
          [401, 403, 404].includes(error.status)
        ) && failureCount < 1,
    },
  },
});
